/**
 * UnifiedAiAssistant Component
 *
 * Full-screen LifeFit360 AI Assistant interface with unified session management.
 * Merges sessions from both Subject Management AI and Reports AI.
 * Routes to appropriate API based on session source and current context.
 */

import React, { useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser, selectCurrentProject } from '../../store/slices/user';
import UnifiedSessionListSidebar from './UnifiedSessionListSidebar';
import ChatPanel from '../../modules/reports/components/reports-ai-assistant/ChatPanel';
import useUnifiedSessions from '../../hooks/useUnifiedSessions';
import useSmartApiClient from '../../hooks/useSmartApiClient';
import '../../modules/reports/components/reports-ai-assistant/reports-ai-assistant.css';

const UnifiedAiAssistant = () => {
  const { sessionId, projectCode } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const currentUser = useSelector(selectUser);
  const currentProject = useSelector(selectCurrentProject);
  const refreshTimeoutRef = useRef(null);

  // Load unified sessions from both APIs
  const {
    sessions,
    loading,
    error,
    warning,
    createSession,
    deleteSession,
    refreshSessions
  } = useUnifiedSessions(currentUser?.id);

  // Find active session to determine its source
  const activeSession = sessions.find(s => s.id === sessionId);

  // Get smart API client (uses session source if available, otherwise route)
  const { apiClient, progressHook, defaultTitle, contextType } = useSmartApiClient(
    pathname,
    activeSession
  );

  // Build unified context
  const unifiedContext = {
    currentRoute: pathname,
    projectId: currentProject?.id || projectCode,
    activeFilters: {},
    visibleDataSummary: null,
    siteIds: null
  };

  // Handle session selection
  const handleSelectSession = useCallback((newSessionId) => {
    // Determine base path from current route
    const basePath = pathname.includes('/subject-management') 
      ? `/${projectCode}/subject-management/ai-assistant`
      : `/${projectCode}/analytics/ai-assistant`;
    
    navigate(`${basePath}/${newSessionId}`);
  }, [navigate, projectCode, pathname]);

  // Handle session creation
  const handleCreateSession = useCallback(async () => {
    try {
      // Create session via appropriate API based on current context
      const newSession = await createSession(apiClient, defaultTitle, {
        purpose: contextType === 'subject-management' 
          ? 'subject-management-assistant' 
          : 'reports-assistant'
      });

      // Determine base path from current route
      const basePath = pathname.includes('/subject-management') 
        ? `/${projectCode}/subject-management/ai-assistant`
        : `/${projectCode}/analytics/ai-assistant`;
      
      // Navigate to new session
      navigate(`${basePath}/${newSession.id}`);
      
      // Refresh session list
      await refreshSessions();
    } catch (err) {
      console.error('Failed to create session:', err);
      throw err;
    }
  }, [apiClient, contextType, defaultTitle, createSession, navigate, projectCode, refreshSessions, pathname]);

  // Handle session deletion
  const handleDeleteSession = useCallback(async (deletedSessionId) => {
    try {
      // Find session to get its source
      const sessionToDelete = sessions.find(s => s.id === deletedSessionId);
      if (!sessionToDelete) return;

      // Delete via appropriate API
      await deleteSession(deletedSessionId, sessionToDelete.source, true);

      // If we're viewing the deleted session, navigate back to list
      if (sessionId === deletedSessionId) {
        // Determine base path from current route
        const basePath = pathname.includes('/subject-management') 
          ? `/${projectCode}/subject-management/ai-assistant`
          : `/${projectCode}/analytics/ai-assistant`;
        
        navigate(basePath);
      }
    } catch (err) {
      console.error('Failed to delete session:', err);
      throw err;
    }
  }, [sessions, deleteSession, sessionId, navigate, projectCode, pathname]);

  // Handle session rename (optional - pass through to backend)
  const handleRenameSession = useCallback((renamedSessionId, newTitle) => {
    console.log(`Session ${renamedSessionId} renamed to: ${newTitle}`);
    // Could implement actual rename via API if needed
  }, []);

  // Handle title update (refresh sidebar to show new title)
  const handleTitleUpdated = useCallback(() => {
    // Clear any pending refresh
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Schedule refresh with debounce (1.5s to ensure backend has updated)
    refreshTimeoutRef.current = setTimeout(() => {
      console.log('[UnifiedAiAssistant] Refreshing sessions after message sent');
      refreshSessions();
      refreshTimeoutRef.current = null;
    }, 1500);
  }, [refreshSessions]);

  // Error state
  if (!currentUser || !currentUser.id) {
    return (
      <div className="reports-ai-assistant">
        <div className="reports-ai-assistant__error">
          <p>You must be logged in to use the AI Assistant</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-ai-assistant">
      <UnifiedSessionListSidebar
        userId={currentUser.id}
        sessions={sessions}
        loading={loading}
        error={error}
        warning={warning}
        activeSessionId={sessionId}
        onSelectSession={handleSelectSession}
        onCreateSession={handleCreateSession}
        onDeleteSession={handleDeleteSession}
        onRenameSession={handleRenameSession}
      />
      
      <ChatPanel
        sessionId={sessionId}
        userId={currentUser.id}
        context={unifiedContext}
        onTitleUpdated={handleTitleUpdated}
        apiClient={apiClient}
        progressTrackingHook={progressHook}
        title={activeSession?.title || ""}
        sessionSource={activeSession?.source}
      />
    </div>
  );
};

export default UnifiedAiAssistant;
